import { useEffect, useRef, useState } from "react";

/**
 * Smooth count-up animation for impact stats.
 * Triggered when the element scrolls into view.
 *
 * Props:
 *  - value: string like "47 000+", "12", "1 250 €", "98%"
 *  - duration: ms (default 1800)
 */
const AnimatedCounter = ({ value, duration = 1800 }) => {
  const [displayed, setDisplayed] = useState("0");
  const elRef = useRef(null);
  const startedRef = useRef(false);

  useEffect(() => {
    const el = elRef.current;
    if (!el) return;

    // Parse: extract leading number + optional prefix/suffix
    const match = String(value).match(/^([^\d-]*)([\d\s.,]+)(.*)$/);
    if (!match) {
      setDisplayed(value);
      return;
    }
    const prefix = match[1] || "";
    const numericStr = match[2].replace(/\s/g, "").replace(",", ".");
    const target = parseFloat(numericStr);
    const suffix = match[3] || "";

    if (isNaN(target)) {
      setDisplayed(value);
      return;
    }

    const formatNumber = (n) => {
      // If decimals present in original, keep them; otherwise integer
      if (numericStr.includes(".")) return n.toFixed(1);
      // Group thousands with non-breaking spaces (FR style)
      return Math.round(n).toString().replace(/\B(?=(\d{3})+(?!\d))/g, "\u202F");
    };

    const animate = () => {
      if (startedRef.current) return;
      startedRef.current = true;
      const start = performance.now();
      const tick = (now) => {
        const t = Math.min(1, (now - start) / duration);
        // easeOutCubic
        const eased = 1 - Math.pow(1 - t, 3);
        setDisplayed(prefix + formatNumber(target * eased) + suffix);
        if (t < 1) requestAnimationFrame(tick);
      };
      requestAnimationFrame(tick);
    };

    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) animate();
        });
      },
      { threshold: 0.3 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [value, duration]);

  return <span ref={elRef}>{displayed}</span>;
};

export default AnimatedCounter;
